'use client';

import { useState, useMemo } from 'react';
import { Search, ArrowUpDown } from 'lucide-react';

interface Constituent {
  symbol: string;
  name: string;
  sector: string;
  marketCap?: number;
}

type SortKey = 'symbol' | 'marketCap';

interface ConstituentTableProps {
  data: Constituent[];
  onSelectSymbol: (symbol: string) => void;
}

function formatMarketCap(value: number | undefined): string {
  if (!value) return '—';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
  return `$${value}`;
}

export function ConstituentTable({ data, onSelectSymbol }: ConstituentTableProps) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('marketCap');

  const filtered = useMemo(() => {
    let result = data;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) => c.symbol.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
      );
    }
    if (sortKey === 'marketCap') {
      result = [...result].sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
    } else {
      result = [...result].sort((a, b) => a.symbol.localeCompare(b.symbol));
    }
    return result;
  }, [data, search, sortKey]);

  return (
    <div className="bg-[#111827] border border-[#1F2937] flex flex-col">
      {/* Search + Sort */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1F2937]">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[#6B7280]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by symbol or name..."
            className="w-full bg-[#1A2035] border border-[#1F2937] pl-7 pr-3 py-1.5 text-xs font-mono text-[#E5E7EB] placeholder:text-[#6B7280] focus:outline-none focus:border-[var(--color-accent)]"
          />
        </div>
        <button
          onClick={() => setSortKey((k) => (k === 'symbol' ? 'marketCap' : 'symbol'))}
          className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-mono uppercase tracking-wider border border-[#1F2937] text-[#6B7280] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] transition-colors whitespace-nowrap"
        >
          <ArrowUpDown className="w-3 h-3" />
          {sortKey === 'marketCap' ? 'Mkt Cap' : 'A–Z'}
        </button>
      </div>

      {/* Header — # and Sector columns are hidden on mobile */}
      <div className="grid grid-cols-[64px_1fr_80px] md:grid-cols-[40px_72px_1fr_100px_120px] gap-x-3 px-3 py-1.5 border-b border-[#1F2937] text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">
        <span className="hidden md:block">#</span>
        <span>Symbol</span>
        <span>Name</span>
        <span className="text-right">Mkt Cap</span>
        <span className="hidden md:block text-right">Sector</span>
      </div>

      {/* Rows */}
      <div className="overflow-y-auto max-h-[400px] divide-y divide-[#1F2937]/50">
        {filtered.map((c, i) => (
          <button
            key={c.symbol}
            onClick={() => onSelectSymbol(c.symbol)}
            className="w-full grid grid-cols-[64px_1fr_80px] md:grid-cols-[40px_72px_1fr_100px_120px] gap-x-3 px-3 py-1.5 text-left hover:bg-[#1A2035] transition-colors"
          >
            <span className="hidden md:block font-mono text-[10px] tabular-nums text-[#6B7280]">{i + 1}</span>
            <span className="font-mono text-xs font-medium text-[var(--color-accent)]">{c.symbol}</span>
            <span className="text-xs text-[#E5E7EB] truncate pr-2">{c.name}</span>
            <span className="font-mono text-[10px] tabular-nums text-[#E5E7EB] text-right">{formatMarketCap(c.marketCap)}</span>
            <span className="hidden md:block text-[10px] text-[#6B7280] truncate text-right">{c.sector}</span>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="px-3 py-4 text-xs text-[#6B7280] text-center">No results found</div>
        )}
      </div>
    </div>
  );
}
